'use client'

import {
  NativeSelect,
  Select as MantineSelect,
  type ComboboxData,
  type ComboboxItem,
  type SelectProps,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { RiArrowDownSLine } from '@remixicon/react'
import { type CSSProperties, useState, type ReactNode } from 'react'

export type CustomSelectProps = SelectProps & {
  /**
   * Custom icon/chevron for the right section.
   * Defaults to RiArrowDownSLine from Remixicon.
   */
  rightSection?: ReactNode
  /**
   * Keep the floating combobox on touch devices too. Off by default: a real
   * <select> gets the platform picker (iOS wheel, Android sheet), which is
   * easier to hit, scrolls with momentum and cannot be clipped by a parent.
   */
  disableNativeOnMobile?: boolean
}

const preventTextSelectionStyles: CSSProperties = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  cursor: 'pointer',
}

/** Flatten Mantine's data shapes (strings, items, groups) to plain items. */
const toItems = (data: ComboboxData | undefined): ComboboxItem[] => {
  if (!data) return []
  return data.flatMap((entry): ComboboxItem[] => {
    if (typeof entry === 'string') return [{ value: entry, label: entry }]
    if ('group' in entry) {
      return (entry.items ?? []).map((item) =>
        typeof item === 'string' ? { value: item, label: item } : item,
      )
    }
    return [entry as ComboboxItem]
  })
}

export const Select = ({
  rightSection,
  rightSectionPointerEvents = 'none',
  onDropdownOpen,
  onDropdownClose,
  styles,
  classNames,
  disableNativeOnMobile = false,
  ...props
}: CustomSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  // Undefined until the media query resolves on the client, so SSR and the
  // first paint keep the desktop control rather than flashing the other one.
  const isMobile = useMediaQuery('(max-width: 768px)')

  const defaultRightSection = (
    <RiArrowDownSLine className="w-4 h-4 text-neutral-800" />
  )

  const rotatingRightSection = (
    <span
      className={`transition-transform duration-200 inline-flex ${isOpen ? 'rotate-180' : ''}`}
    >
      {rightSection ?? defaultRightSection}
    </span>
  )

  const mergedStyles = typeof styles === 'function'
    ? styles
    : {
        ...styles,
        input: {
          ...preventTextSelectionStyles,
          ...((styles as any)?.input || {}),
        },
        wrapper: {
          userSelect: 'none',
          WebkitUserSelect: 'none',
          ...((styles as any)?.wrapper || {}),
        },
      }

  // A searchable select exists because its list is long (banks, countries).
  // A native <select> has no filtering, so those keep the combobox.
  if (isMobile && !disableNativeOnMobile && !props.searchable) {
    // Combobox-only props have no meaning on a native <select>; pulling them
    // out keeps them off the DOM node.
    const {
      data,
      value,
      defaultValue,
      onChange,
      placeholder,
      allowDeselect: _allowDeselect,
      searchable: _searchable,
      clearable: _clearable,
      comboboxProps: _comboboxProps,
      nothingFoundMessage: _nothingFoundMessage,
      renderOption: _renderOption,
      onSearchChange: _onSearchChange,
      searchValue: _searchValue,
      limit: _limit,
      maxDropdownHeight: _maxDropdownHeight,
      filter: _filter,
      onOptionSubmit: _onOptionSubmit,
      ...nativeProps
    } = props

    const items = toItems(data)
    // NativeSelect has no placeholder prop; an unselectable first option is the
    // native equivalent, and it is only needed while nothing is chosen.
    const needsPlaceholder = Boolean(placeholder) && !value && !defaultValue

    // Call sites size these for a mouse (30px tall, 13px text). On touch that
    // is under the 44px hit target, and iOS zooms the page whenever a focused
    // control's text is below 16px — so the mobile control sets its own floor.
    const nativeStyles =
      typeof mergedStyles === 'function'
        ? mergedStyles
        : {
            ...mergedStyles,
            input: {
              ...((mergedStyles as any)?.input || {}),
              minHeight: 44,
              height: 44,
              fontSize: 16,
            },
          }

    return (
      <NativeSelect
        {...(nativeProps as any)}
        data={
          needsPlaceholder
            ? [{ value: '', label: String(placeholder), disabled: true }, ...items]
            : items
        }
        value={value ?? undefined}
        defaultValue={defaultValue ?? undefined}
        rightSection={rightSection ?? defaultRightSection}
        rightSectionPointerEvents="none"
        styles={nativeStyles}
        classNames={classNames}
        onChange={(event) => {
          const next = event.currentTarget.value
          onChange?.(
            next,
            items.find((item) => item.value === next) ?? { value: next, label: next },
          )
        }}
      />
    )
  }

  return (
    <MantineSelect
      rightSection={rotatingRightSection}
      rightSectionPointerEvents={rightSectionPointerEvents}
      onDropdownOpen={() => {
        setIsOpen(true)
        onDropdownOpen?.()
      }}
      onDropdownClose={() => {
        setIsOpen(false)
        onDropdownClose?.()
      }}
      styles={mergedStyles}
      classNames={classNames}
      comboboxProps={{
        transitionProps: { transition: 'fade', duration: 150 },
      }}
      onMouseDown={(e) => {
        if (e.detail > 1) {
          e.preventDefault()
        }
      }}
      {...props}
    />
  )
}
